import { Component, ChangeDetectionStrategy, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FeedbackModalService } from '../../services/feedback-modal.service';
import { FeedbackService, FeedbackType, FeedbackCategory } from '../../services/api/yuload/feedback.service';
import { ToastService } from '../../services/toast.service';
import { ModalComponent } from '../ui/modal/modal.component';
import { ButtonComponent } from '../ui/button/button.component';

interface FeedbackTypeOption {
  value: FeedbackType;
  label: string;
  icon: string;
  description: string;
}

interface CategoryOption {
  value: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-feedback-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ModalComponent, ButtonComponent],
  template: `
    @if (modalService.state().isOpen) {
      <app-modal [isOpen]="modalService.state().isOpen" [title]="modalTitle()" [size]="'md'" (closed)="close()">
        <div class="feedback-modal-content">
          <!-- Entity info -->
          @if (config()?.entityName) {
            <div class="entity-info">
              <i class="fas fa-tag"></i>
              <span>{{ config()?.entityName }}</span>
            </div>
          }

          <!-- Feedback Type Selection -->
          <div class="form-section">
            <label class="section-label">Tipo de feedback</label>
            <div class="feedback-types">
              @for (type of feedbackTypes; track type.value) {
                <button
                  type="button"
                  class="type-btn"
                  [class.selected]="selectedType() === type.value"
                  (click)="selectType(type.value)"
                >
                  <i [class]="type.icon"></i>
                  <span class="type-label">{{ type.label }}</span>
                  <span class="type-description">{{ type.description }}</span>
                </button>
              }
            </div>
          </div>

          <!-- Category Selection -->
          @if (availableCategories().length > 0) {
            <div class="form-section">
              <label class="section-label">Categoria (opcional)</label>
              <div class="categories">
                @for (cat of availableCategories(); track cat.value) {
                  <button
                    type="button"
                    class="category-btn"
                    [class.selected]="selectedCategory() === cat.value"
                    (click)="selectCategory(cat.value)"
                  >
                    <i [class]="cat.icon"></i>
                    {{ cat.label }}
                  </button>
                }
              </div>
            </div>
          }

          <!-- Content -->
          <div class="form-section">
            <label class="section-label">Comentario (opcional)</label>
            <textarea
              class="feedback-textarea"
              [(ngModel)]="content"
              placeholder="Describe tu feedback..."
              rows="4"
              maxlength="5000"
            >
            </textarea>
            <div class="char-count">{{ content().length }} / 5000</div>
          </div>

          <!-- Actions -->
          <div class="modal-actions">
            <app-button [config]="{ variant: 'outline', size: 'md' }" (buttonClick)="close()"> Cancelar </app-button>
            <app-button
              [config]="{
                variant: 'primary',
                size: 'md',
                disabled: !isValid() || isSubmitting(),
              }"
              (buttonClick)="submit()"
            >
              @if (isSubmitting()) {
                <i class="fas fa-spinner fa-spin"></i>
                Enviando...
              } @else {
                <i class="fas fa-paper-plane"></i>
                Enviar Feedback
              }
            </app-button>
          </div>
        </div>
      </app-modal>
    }
  `,
  styles: [
    `
      .feedback-modal-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-lg);
      }

      .entity-info {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        padding: var(--space-sm) var(--space-md);
        background: var(--bg-secondary);
        border-radius: var(--radius);
        font-size: var(--text-sm);
        color: var(--text-secondary);
      }

      .entity-info i {
        color: var(--text-tertiary);
      }

      .form-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
      }

      .section-label {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--text-primary);
      }

      /* Feedback Types */
      .feedback-types {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-sm);
      }

      .type-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-xs);
        padding: var(--space-md);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius);
        background: var(--bg-primary);
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;
      }

      .type-btn:hover {
        background: var(--bg-secondary);
        border-color: var(--border-secondary);
      }

      .type-btn.selected {
        background: var(--bg-accent);
        border-color: var(--accent-color);
      }

      .type-btn i {
        font-size: var(--text-xl);
        color: var(--text-tertiary);
      }

      .type-btn.selected i {
        color: var(--accent-color);
      }

      .type-label {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--text-primary);
      }

      .type-description {
        font-size: var(--text-xs);
        color: var(--text-tertiary);
      }

      /* Categories */
      .categories {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-xs);
      }

      .category-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-xs);
        padding: var(--space-xs) var(--space-sm);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius-full);
        background: var(--bg-primary);
        font-size: var(--text-xs);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .category-btn:hover {
        background: var(--bg-secondary);
        border-color: var(--border-secondary);
      }

      .category-btn.selected {
        background: var(--bg-accent);
        border-color: var(--accent-color);
        color: var(--accent-color);
      }

      .category-btn i {
        font-size: 10px;
      }

      /* Textarea */
      .feedback-textarea {
        width: 100%;
        padding: var(--space-md);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius);
        background: var(--bg-primary);
        color: var(--text-primary);
        font-size: var(--text-sm);
        font-family: inherit;
        resize: vertical;
        min-height: 100px;
        transition: border-color 0.2s ease;
      }

      .feedback-textarea:focus {
        outline: none;
        border-color: var(--accent-color);
      }

      .feedback-textarea::placeholder {
        color: var(--text-tertiary);
      }

      .char-count {
        font-size: var(--text-xs);
        color: var(--text-tertiary);
        text-align: right;
      }

      /* Actions */
      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-sm);
        padding-top: var(--space-md);
        border-top: 1px solid var(--border-primary);
      }

      /* Responsive */
      @media (max-width: 480px) {
        .feedback-types {
          grid-template-columns: 1fr;
        }

        .modal-actions {
          flex-direction: column;
        }

        .modal-actions app-button {
          width: 100%;
        }
      }
    `,
  ],
})
export class FeedbackModalComponent {
  readonly modalService = inject(FeedbackModalService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly toastService = inject(ToastService);

  // Form state
  selectedType = signal<FeedbackType | null>(null);
  selectedCategory = signal<string | null>(null);
  content = signal<string>('');
  isSubmitting = signal<boolean>(false);

  // Feedback type options
  readonly feedbackTypes: FeedbackTypeOption[] = [
    {
      value: 'POSITIVE',
      label: 'Positivo',
      icon: 'fas fa-thumbs-up',
      description: 'Funciona bien',
    },
    {
      value: 'NEGATIVE',
      label: 'Negativo',
      icon: 'fas fa-thumbs-down',
      description: 'Hay un problema',
    },
    {
      value: 'SUGGESTION',
      label: 'Sugerencia',
      icon: 'fas fa-lightbulb',
      description: 'Propuesta de mejora',
    },
    {
      value: 'BUG_REPORT',
      label: 'Bug',
      icon: 'fas fa-bug',
      description: 'Error de aplicacion',
    },
  ];

  // Category label and icon mappings
  private readonly categoryLabels: Record<string, { label: string; icon: string }> = {
    accuracy: { label: 'Precisión', icon: 'fas fa-crosshairs' },
    data_quality: { label: 'Calidad datos', icon: 'fas fa-database' },
    usability: { label: 'Usabilidad', icon: 'fas fa-hand-pointer' },
    performance: { label: 'Rendimiento', icon: 'fas fa-tachometer-alt' },
    missing_feature: { label: 'Funcionalidad', icon: 'fas fa-puzzle-piece' },
    ui_ux: { label: 'UI/UX', icon: 'fas fa-paint-brush' },
  };

  // Computed values
  config = computed(() => this.modalService.state().config);

  modalTitle = computed(() => {
    const cfg = this.config();
    if (cfg?.entityName) {
      return `Feedback: ${cfg.entityName}`;
    }
    return 'Enviar Feedback';
  });

  availableCategories = computed<CategoryOption[]>(() => {
    const cfg = this.config();
    if (!cfg?.allowedCategories || cfg.allowedCategories.length === 0) {
      return [];
    }
    return cfg.allowedCategories.map((categoryId) => {
      const mapping = this.categoryLabels[categoryId];
      return {
        value: categoryId,
        label: mapping?.label || this.formatCategoryLabel(categoryId),
        icon: mapping?.icon || 'fas fa-tag',
      };
    });
  });

  /**
   * Format category ID to a readable label (fallback for unknown categories)
   */
  private formatCategoryLabel(categoryId: string): string {
    return categoryId
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  isValid = computed(() => {
    return this.selectedType() !== null;
  });

  constructor() {
    // Reset form when modal opens
    effect(() => {
      const state = this.modalService.state();
      if (state.isOpen) {
        this.resetForm();
        if (state.config?.defaultFeedbackType) {
          this.selectedType.set(state.config.defaultFeedbackType);
        }
      }
    });
  }

  selectType(type: FeedbackType): void {
    this.selectedType.set(type);
  }

  selectCategory(category: string): void {
    // Toggle selection
    if (this.selectedCategory() === category) {
      this.selectedCategory.set(null);
    } else {
      this.selectedCategory.set(category);
    }
  }

  close(): void {
    this.modalService.close();
    this.resetForm();
  }

  submit(): void {
    const cfg = this.config();
    const type = this.selectedType();
    const contentText = this.content().trim();

    if (!cfg || !type) {
      return;
    }

    this.isSubmitting.set(true);

    this.feedbackService
      .createFeedback({
        entityType: cfg.entityType,
        entityId: cfg.entityId,
        feedbackType: type,
        content: contentText,
        category: this.selectedCategory() || undefined,
        metadata: {
          ...cfg.metadata,
          entityName: cfg.entityName,
        },
      })
      .subscribe({
        next: () => {
          this.toastService.success('Feedback enviado correctamente');
          this.close();
        },
        error: (error) => {
          console.error('Error sending feedback:', error);
          this.toastService.error('Error al enviar el feedback');
          this.isSubmitting.set(false);
        },
      });
  }

  private resetForm(): void {
    this.selectedType.set(null);
    this.selectedCategory.set(null);
    this.content.set('');
    this.isSubmitting.set(false);
  }
}
